import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import {
  addMinutes,
  parseISO,
  format,
  startOfWeek,
  addDays,
  endOfDay,
  isAfter,
  isBefore,
} from 'date-fns';

import { useToast } from '../../hooks/toast';
import { useAgendamento } from '../../hooks/agendamento';

import api from '../../services/api';

import SelectSync, { IOption } from '../SelectSync';
import Input from '../Input';
import Loading from '../Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
  TIPO_PERFIL,
} from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import { IOrcamento } from '../../pages/PacienteAtendimento/Odonto';
import { IAgendamento } from '../Agendamento';
import { useAuth } from '../../hooks/auth';
import { PermissaoAdmin } from '../Agendamento/styles';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
  ProDuracao: number;
}

interface IFormData {
  AgeData: Date;
  AgeHora: string;
  AgeConfCodigo: string;
  login?: string;
  senha?: string;
}

export interface IReturnAgendamentoTratamento {
  AgeCodigo: number;
  AgeInicio: Date;
  AgeInicioFormatado: string;
  AgeConfCodigo: number;
  updated_at: Date;
  valor: number;
}

interface IDadosAgendamento {
  ConCodigo: number;
  AgeClinica: number;
}

interface IAgendamentoAdicionado extends IAgendamento {
  AgeCodigo: number;
}

type ComponentProps = {
  orcamentoId: number;
  perfil?: number;
  proCodigo?: number;
  agendamentos: IReturnAgendamentoTratamento[];
  handleFechar(dados: IReturnAgendamentoTratamento): void;
  limites: { semanal: number; total: number };
  validade: Date;
};

const PacoteAgendamento: React.FC<ComponentProps> = ({
  orcamentoId,
  perfil,
  agendamentos,
  limites,
  validade,
  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { clinica } = useAuth();
  const { addToast } = useToast();
  const { buscaProcedimentos } = useAgendamento();
  const [procedimento, setProcedimento] = useState<IProcedimento | undefined>();
  const [loading, setLoading] = useState(false);
  const [orcamento, setOrcamento] = useState<IOrcamento>();
  const [optionsAgendas, setOptionsAgendas] = useState<IOption[] | undefined>(
    undefined,
  );
  const [exibePermissaoAdmin, setExibePermissaoAdmin] = useState(false);
  const [mensagemPermissaoAdmin, setMensagemPermissaoAdmin] = useState('');

  const [finalizado, setFinalizado] = useState(false);

  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IOrcamento>(`tratamento/${orcamentoId}`);
        const dadosOrcamento = response.data;
        if (dadosOrcamento.finalizado) {
          setFinalizado(true);
        } else if (perfil) {
          // selecionar agendas do perfil

          const responseAgenda = await api.get<IOption[]>(
            `/agendas/porperfil?ConPerCodigo=${perfil}&ConClinica=${clinica.CliCodigo}`,
          );

          setOptionsAgendas(responseAgenda.data);

          const respostaPro = await buscaProcedimentos(perfil.toString());

          if (respostaPro) {
            const tratamento = respostaPro.procedimentos.find(
              proc =>
                proc.ProCodigo ===
                dadosOrcamento.procedimentosTratamento[0].ProCodigo,
            );
            setProcedimento(tratamento);
          }
        }

        setOrcamento(response.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (orcamentoId) loadDados();
  }, [clinica.CliCodigo, perfil, orcamentoId, addToast, buscaProcedimentos]);

  // const total = orcamento?.valor || 0;
  // const totalAgendado = orcamento?.agendamentosTratamento.reduce((tot, age) => {
  //   return (
  //     tot +
  //     age.agenda.procedimentosValorCobrado.reduce((tot2, proc) => {
  //       return tot2 + Number(proc.ProValor);
  //     }, 0)
  //   );
  // }, 0);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        if (!orcamento) throw new Error('Orçamento não encontrado');
        if (!procedimento) throw new Error('Procedimento não encontrados');

        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
          AgeConfCodigo: Yup.string().required('Agenda obrigatória'),
          ...(exibePermissaoAdmin
            ? {
                login: Yup.string().required('Login é obrigatório'),
                senha: Yup.string().required('Senha é obrigatória'),
              }
            : {}),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamentoContrato = agendamentos.find(age => age.valor > 0);

        if (agendamentoContrato) {
          if (
            isBefore(
              dataAgendamento,
              parseISO(agendamentoContrato.AgeInicio.toString()),
            )
          )
            throw new Error(
              `Data do agendamento não pode ser menor que a data do primeiro agendamento: ${agendamentoContrato.AgeInicioFormatado}`,
            );
        }

        // verifica Limite

        const InicioSemana = startOfWeek(dataAgendamento, { weekStartsOn: 1 });
        const FinalSemana = endOfDay(addDays(InicioSemana, 5));

        // console.log(InicioSemana, FinalSemana);

        const agendamentosSemana = agendamentos.filter(
          age =>
            parseISO(age.AgeInicio.toString()) >= InicioSemana &&
            parseISO(age.AgeInicio.toString()) <= FinalSemana,
        ).length;

        if (agendamentosSemana >= limites.semanal)
          throw new Error(
            `Numero de agendamentos semanal atingido: ${limites.semanal}`,
          );

        if (isAfter(dataAgendamento, endOfDay(parseISO(validade.toString()))))
          throw new Error(
            `Agendamento disponíveis apenas até a data de validade: ${format(
              parseISO(validade.toString()),
              'dd/MM/yyyy',
            )}`,
          );

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, procedimento.ProDuracao),
          AgePacCodigo: orcamento.PacCodigo,
          AgeFormaPag: 9,
          AgeClinica: clinica.CliCodigo,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: orcamento.plano,
          AgeTipo: TIPO_PERFIL.CONSULTA,
          AgeTratamento: 2,
          ...(exibePermissaoAdmin
            ? { permissao: { UsuLogin: dados.login, UsuSenha: dados.senha } }
            : {}),
          duracao: procedimento.ProDuracao,
          procedimentos: [
            {
              ProCodigo: procedimento.ProCodigo,
              ProNome: procedimento.ProNome,
              ProValor: parseFloat(
                (orcamento.valor / orcamento.secoes).toFixed(2),
              ),
            },
          ],
        };

        const agendamentoAdicionado = await api.post<IAgendamentoAdicionado>(
          `/agendamentos/tratamento/${orcamento.id}`,
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda

        addToast({ title: 'Agendamento salvo', type: 'success' });

        const valorAgendamento = agendamentoAdicionado.data.procedimentos.reduce(
          (tot, proc) => {
            return tot + Number(proc.ProValorCobrado);
          },
          0,
        );

        handleFechar({
          AgeCodigo: agendamentoAdicionado.data.AgeCodigo,
          AgeInicio: agendamentoAdicionado.data.AgeInicio,
          AgeInicioFormatado: format(
            parseISO(agendamentoAdicionado.data.AgeInicio.toString()),
            'dd/MM/yyyy HH:mm',
          ),
          AgeConfCodigo: agendamentoAdicionado.data.AgeConfCodigo,
          updated_at: agendamentoAdicionado.data.updated_at,
          valor: valorAgendamento,
        });
      } catch (error: any) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        const statusErro = error?.response?.status;
        if (statusErro === 403) {
          setExibePermissaoAdmin(true);
          setMensagemPermissaoAdmin(mensagemErro);
        }
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
          timeAlive: 10000,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      orcamento,
      procedimento,
      clinica.CliCodigo,
      agendamentos,
      handleFechar,
      limites.semanal,
      validade,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      {finalizado ? (
        <h5 className="text-center">Status: Pedido de Exame Finalizado</h5>
      ) : (
        <>
          <h5>Gerar agendamento</h5>
          {optionsAgendas && (
            <Form
              ref={formRef}
              initialData={initialData}
              onSubmit={handleSubmit}
            >
              {exibePermissaoAdmin && (
                <PermissaoAdmin className="shadow border">
                  <div>
                    <h6>Permissão necessária</h6>
                    <p>{mensagemPermissaoAdmin}</p>
                    <Input name="login" label="Código ou Email" />
                    <Input
                      name="senha"
                      label="Senha"
                      autoComplete="off"
                      type="password"
                    />
                    <Button
                      type="submit"
                      variant="primary mt-2"
                      size="sm"
                      disabled={loading}
                    >
                      {loading ? 'Enviando...' : 'Confirmar'}
                    </Button>

                    <Button
                      type="button"
                      variant="outline-secondary mt-2 float-right"
                      size="sm"
                      disabled={loading}
                      onClick={() => {
                        setExibePermissaoAdmin(false);
                        setMensagemPermissaoAdmin('');
                      }}
                    >
                      Voltar
                    </Button>
                  </div>
                </PermissaoAdmin>
              )}
              <Row className="mb-2">
                <Col md={5} sm={6}>
                  <SelectSync
                    name="AgeConfCodigo"
                    label="Agenda"
                    placeholder="Selecione a agenda"
                    isClearable={false}
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsAgendas}
                  />
                </Col>
                <Col md={3} sm={6}>
                  <Input name="AgeData" label="Data" type="date" />
                </Col>
                <Col md={2} sm={6}>
                  <SelectSync
                    name="AgeHora"
                    label="Hora"
                    placeholder="Selecione"
                    isClearable={false}
                    handleSelectChange={handleOnChangeVazio}
                    options={optionsHora}
                  />
                </Col>
                <Col md={2} sm={6}>
                  <br />
                  <Button
                    type="submit"
                    size="sm"
                    className="mt-1"
                    disabled={loading}
                  >
                    Gerar
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </>
      )}
    </Container>
  );
};

export default PacoteAgendamento;
